/**
 * PhamViCongTy — chạy một phần giao diện (thường là một widget dashboard) bằng DỮ LIỆU của công ty
 * khác công ty đang đăng nhập. Xem giải thích đầy đủ ở dashboardCongTy.js.
 *
 *   <PhamViCongTy id_app="64f0…">
 *     <CounterWidget code="dh2" />          // đếm đơn hàng của công ty 64f0…
 *   </PhamViCongTy>
 *
 * Kèm theo:
 *   - useDsCongTy(): các công ty người dùng được vào (để chọn và để hiện tên).
 *   - DemNhieuCongTy: CỘNG TỔNG một CounterWidget trên nhiều công ty.
 */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import AuthContext from './auth/Context';
import CounterWidget from './Counter';
import { numeral } from '../utils';
import { taoNguCanhCongTy, tongSoCongTy } from './dashboardCongTy';

/**
 * Bộ lọc công ty CHUNG của dashboard (DashboardLocCongTy.jsx đặt giá trị): `null` = mặc định (công ty
 * đang đăng nhập — widget theo cấu hình riêng), mảng id = mọi widget hỗ trợ lấy dữ liệu các công ty này.
 */
export const LocCongTyContext = React.createContext(null);

const layToken = (context) => context?.userInfo?.token || (typeof context?.getToken === 'function' ? context.getToken() : '');
const chuLoi = (e) => (e && (e.message || e.error)) || (typeof e === 'string' ? e : 'Không rõ lỗi');

/** Các công ty người dùng đang tham gia — model `app` không theo id_app, máy chủ tự lọc theo participant. */
export function useDsCongTy(enabled = true) {
	const context = useContext(AuthContext);
	const goc = context?.nguCanhGoc || context;
	const token = layToken(goc);
	const { data = [], isLoading } = useQuery({
		queryKey: ['ds_cong_ty_cua_toi', goc?.userInfo?.email],
		queryFn: async () => {
			const res = await goc.apis.asyncGetList(token, 'app', { limit: 1000, fields: '_id,name' });
			const ds = res?.data || res || [];
			return (Array.isArray(ds) ? ds : []).map((a) => ({ _id: String(a._id), name: a.name || String(a._id) }));
		},
		enabled: !!enabled && !!token,
		staleTime: 10 * 60 * 1000,
		refetchOnWindowFocus: false,
	});
	return { dsCongTy: data, isLoading };
}

/** Tên công ty theo id (lấy từ danh sách công ty; chưa có thì trả id). */
export function useTenCongTy(enabled = true) {
	const { dsCongTy } = useDsCongTy(enabled);
	const context = useContext(AuthContext);
	const goc = context?.nguCanhGoc || context;
	return useCallback((id) => {
		if (!id) return '';
		if (goc?.appInfo && String(goc.appInfo._id) === String(id)) return goc.appInfo.name || String(id);
		const c = dsCongTy.find((x) => x._id === String(id));
		return c ? c.name : String(id);
	}, [dsCongTy, goc?.appInfo]);
}

export default function PhamViCongTy({ id_app, children, renderRunning, renderError }) {
	const context = useContext(AuthContext);
	const goc = context?.nguCanhGoc || context;
	const idHienTai = goc?.appInfo?._id ? String(goc.appInfo._id) : '';
	const laCongTyHienTai = !id_app || String(id_app) === idHienTai;
	const token = layToken(goc);

	const { data: appInfo, isLoading, error } = useQuery({
		queryKey: ['app_info_pham_vi', String(id_app || '')],
		queryFn: async () => {
			let rs = await goc.apis.asyncGetAppInfo(token, String(id_app), '', { cache: true, cache_local: false });
			if (typeof rs === 'string') rs = JSON.parse(rs);
			if (!rs || !rs._id) throw new Error(`Không đọc được thông tin công ty '${id_app}'`);
			return rs;
		},
		enabled: !laCongTyHienTai && !!token,
		staleTime: 30 * 60 * 1000,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const nguCanh = useMemo(
		() => (laCongTyHienTai || !appInfo ? null : taoNguCanhCongTy(goc, appInfo)),
		[laCongTyHienTai, appInfo, goc]
	);

	// Công ty đang đăng nhập: trả lại ngữ cảnh GỐC (kể cả khi lồng trong một phạm vi khác).
	if (laCongTyHienTai) {
		return context === goc ? children : <AuthContext.Provider value={goc}>{children}</AuthContext.Provider>;
	}
	if (error) {
		if (renderError) return renderError(error);
		return (
			<Box sx={{ p: 2, color: 'error.main' }}>
				<Typography variant="body2">Không xem được dữ liệu công ty này: {chuLoi(error)}</Typography>
			</Box>
		);
	}
	if (isLoading || !nguCanh) {
		if (renderRunning) return renderRunning();
		return <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress size={22} /></Box>;
	}
	return <AuthContext.Provider value={nguCanh}>{children}</AuthContext.Provider>;
}

PhamViCongTy.propTypes = {
	id_app: PropTypes.string,
	children: PropTypes.node,
	renderRunning: PropTypes.func,
	renderError: PropTypes.func,
};

// ───────────────────────────── ĐẾM CỘNG TỔNG NHIỀU CÔNG TY ─────────────────────────────

/** Báo một giá trị lên cha SAU khi vẽ (không setState của cha ngay trong lúc vẽ). */
function BaoLen({ id, n, onSo }) {
	React.useEffect(() => { onSo(id, n); }, [id, n, onSo]);
	return null;
}

/**
 * Đếm của MỘT công ty — không vẽ gì, chỉ báo số lên cha. Dùng `render` của CounterWidget chứ KHÔNG dùng
 * `onChange`: onChange bắn cả số 0 mặc định lúc còn đang tải, cha sẽ tưởng công ty đó đã về số 0.
 */
const DemMotCongTy = React.memo(function DemMotCongTy({ id_app, code, condition, refreshTrigger, onSo }) {
	return (
		<PhamViCongTy id_app={id_app} renderRunning={() => null} renderError={() => <BaoLen id={id_app} n={null} onSo={onSo} />}>
			<CounterWidget
				code={code}
				condition={condition}
				refreshTrigger={refreshTrigger}
				renderRunning={() => null}
				render={(n) => <BaoLen id={id_app} n={Number(n) || 0} onSo={onSo} />}
			/>
		</PhamViCongTy>
	);
});

/**
 * Tổng của CounterWidget trên nhiều công ty. Hiện số ngay khi có công ty đầu tiên (số tăng dần khi các
 * công ty còn lại về), rê chuột thấy số từng công ty. Công ty lỗi (không có quyền…) hiện "—" và KHÔNG
 * làm hỏng tổng của các công ty còn lại.
 */
export function DemNhieuCongTy({ dsCongTy, code, condition, refreshTrigger, renderRunning }) {
	const tenCongTy = useTenCongTy();
	const [so, setSo] = useState({});
	const onSo = useCallback((id, n) => {
		setSo((cu) => {
			if (n === null) {
				if (cu[id] === 'loi') return cu;
				return { ...cu, [id]: 'loi' };
			}
			if (cu[id] === n) return cu;
			return { ...cu, [id]: n };
		});
	}, []);

	const daCo = useMemo(() => {
		const o = {};
		dsCongTy.forEach((id) => { if (typeof so[id] === 'number') o[id] = so[id]; });
		return o;
	}, [so, dsCongTy]);
	const { tong, soCongTyDaCo } = tongSoCongTy(daCo);
	const coLoi = dsCongTy.some((id) => so[id] === 'loi');

	const chiTiet = (
		<Box sx={{ p: 0.5 }}>
			{dsCongTy.map((id) => (
				<Box key={id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, fontSize: 12 }}>
					<span>{tenCongTy(id)}</span>
					<b>{so[id] === 'loi' ? '—' : (typeof so[id] === 'number' ? numeral(so[id]).format() : '…')}</b>
				</Box>
			))}
		</Box>
	);

	return (
		<>
			{dsCongTy.map((id) => (
				<DemMotCongTy key={id} id_app={id} code={code} condition={condition} refreshTrigger={refreshTrigger} onSo={onSo} />
			))}
			{soCongTyDaCo === 0 && !coLoi && renderRunning ? renderRunning() : (
				<Tooltip title={chiTiet} arrow placement="bottom-start">
					<span>{numeral(tong).format()}{soCongTyDaCo < dsCongTy.length && !coLoi ? '…' : ''}</span>
				</Tooltip>
			)}
		</>
	);
}

DemNhieuCongTy.propTypes = {
	dsCongTy: PropTypes.arrayOf(PropTypes.string).isRequired,
	code: PropTypes.string,
	condition: PropTypes.object,
	refreshTrigger: PropTypes.number,
	renderRunning: PropTypes.func,
};
